import { Box, Skeleton, Typography, styled } from "@mui/material";
import React, { useEffect, useState } from "react";
import { MessageType, PreviewData } from "../../../interfaces/all";
import { linkPreviewService } from "../../../services/linkPreview";
import { useLogger } from "../../../utils/logger";
import { extractLinks } from "../../../utils/message";

const PreviewBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  cursor: "pointer",
  "&:hover": {
    opacity: 0.9,
  },
});

const PreviewCard = styled(Box)({
  border: "1px solid #e9edef",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#fff",
});

const PreviewImage = styled("img")({
  width: "100%",
  height: "200px",
  objectFit: "cover",
});

const PreviewTitle = styled(Typography)({
  color: "#111b21",
  fontWeight: 500,
  marginBottom: 4,
  padding: "8px 12px 0",
});

const PreviewDescription = styled(Typography)({
  color: "#667781",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  padding: "0 12px",
});

const PreviewUrl = styled(Typography)({
  color: "#667781",
  display: "block",
  marginTop: 4,
  padding: "0 12px 8px",
  fontSize: "0.75rem",
});

interface LinkPreviewPropsSkeleton {
  content: MessageType;
  showSkeleton?: boolean;
}

const LinkPreview: React.FC<LinkPreviewPropsSkeleton> = ({
  content,
  showSkeleton = false,
}) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const logger = useLogger("LinkPreview");

  useEffect(() => {
    const fetchPreview = async (url: string) => {
      setIsLoading(true);
      try {
        const preview = await linkPreviewService.getPreview(url);
        if (preview) {
          setPreviewData(preview);
        } else {
          // Fallback para preview básico
          const basicPreview = await linkPreviewService.getBasicPreview(url);
          setPreviewData(basicPreview);
        }
      } catch (error) {
        // Usar logger diretamente sem incluí-lo nas dependências
        if (typeof logger.error === "function") {
          logger.error("Erro ao buscar preview do link", { url, error });
        }
        // Fallback para preview básico em caso de erro
        const basicPreview = await linkPreviewService.getBasicPreview(url);
        setPreviewData(basicPreview);
      } finally {
        setIsLoading(false);
      }
    };

    if (content.content.text) {
      const links = extractLinks(content.content.text);
      if (links.length > 0) {
        fetchPreview(links[0]);
      }
    }
  }, [content.content.text, logger]); // Adicionada dependência logger

  const handlePreviewClick = () => {
    if (previewData?.url) {
      window.open(previewData.url, "_blank", "noopener,noreferrer");
    }
  };

  if (showSkeleton || isLoading) {
    return (
      <PreviewBox>
        <PreviewCard>
          <Skeleton variant="rectangular" width={400} height={200} />
          <PreviewTitle variant="subtitle2">
            <Skeleton variant="text" width={180} height={22} />
          </PreviewTitle>
          <PreviewDescription variant="body2">
            <Skeleton variant="text" width={260} height={18} />
          </PreviewDescription>
          <PreviewUrl variant="caption">
            <Skeleton variant="text" width={120} height={14} />
          </PreviewUrl>
        </PreviewCard>
      </PreviewBox>
    );
  }

  if (!previewData) {
    return null;
  }

  return (
    <PreviewBox onClick={handlePreviewClick}>
      <PreviewCard>
        {previewData.image && (
          <PreviewImage
            src={previewData.image}
            alt={previewData.title}
            onError={(e) => {
              // Remover imagem se falhar ao carregar
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
          />
        )}
        <PreviewTitle variant="subtitle2">{previewData.title}</PreviewTitle>
        <PreviewDescription variant="body2">
          {previewData.description}
        </PreviewDescription>
        <PreviewUrl variant="caption">{previewData.url}</PreviewUrl>
      </PreviewCard>
    </PreviewBox>
  );
};

export default LinkPreview;
